'use client';

import { ReactNode } from 'react';
import Icon from './AppIcon';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

export default function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = 'lg',
}: ModalProps) {
  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
  };

  return (
    <div data-component-id="components/ui/Modal.tsx:31:4" data-component-path="components/ui/Modal.tsx" data-component-line="31" data-component-file="Modal.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22fixed%20inset-0%20z-50%20flex%20items-center%20justify-center%20p-4%22%7D" className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div data-component-id="components/ui/Modal.tsx:33:6" data-component-path="components/ui/Modal.tsx" data-component-line="33" data-component-file="Modal.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20bg-black%2F50%20backdrop-blur-sm%22%7D"
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal Content */}
      <div data-component-id="components/ui/Modal.tsx:40:6" data-component-path="components/ui/Modal.tsx" data-component-line="40" data-component-file="Modal.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
        className={`${sizeClasses[size]} w-full bg-white border border-slate-200 rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto relative z-10`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        {title && (
          <div data-component-id="components/ui/Modal.tsx:46:10" data-component-path="components/ui/Modal.tsx" data-component-line="46" data-component-file="Modal.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20p-6%20border-b%20border-slate-200%20sticky%20top-0%20bg-white%20rounded-t-2xl%22%7D" className="flex items-center justify-between p-6 border-b border-slate-200 sticky top-0 bg-white rounded-t-2xl">
            <h2 data-component-id="components/ui/Modal.tsx:47:12" data-component-path="components/ui/Modal.tsx" data-component-line="47" data-component-file="Modal.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-2xl%20font-bold%20text-slate-900%22%7D" className="text-2xl font-bold text-slate-900">
              {title}
            </h2>
            <button data-component-id="components/ui/Modal.tsx:50:12" data-component-path="components/ui/Modal.tsx" data-component-line="50" data-component-file="Modal.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22inline-flex%20items-center%20justify-center%20w-10%20h-10%20text-slate-500%20hover%3Atext-slate-900%20hover%3Abg-slate-100%20rounded-lg%20transition-colors%22%7D"
              onClick={onClose}
              className="inline-flex items-center justify-center w-10 h-10 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
              aria-label="Close modal"
            >
              <Icon data-component-id="components/ui/Modal.tsx:55:14" data-component-path="components/ui/Modal.tsx" data-component-line="55" data-component-file="Modal.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22XMarkIcon%22%7D" name="XMarkIcon" size={24} />
            </button>
          </div>
        )}

        {/* Close Button (Top Right if no title) */}
        {!title && (
          <button data-component-id="components/ui/Modal.tsx:62:10" data-component-path="components/ui/Modal.tsx" data-component-line="62" data-component-file="Modal.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22absolute%20top-4%20right-4%20inline-flex%20items-center%20justify-center%20w-10%20h-10%20text-slate-500%20hover%3Atext-slate-900%20hover%3Abg-slate-100%20rounded-lg%20transition-colors%20z-20%22%7D"
            onClick={onClose}
            className="absolute top-4 right-4 inline-flex items-center justify-center w-10 h-10 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors z-20"
            aria-label="Close modal"
          >
            <Icon data-component-id="components/ui/Modal.tsx:67:12" data-component-path="components/ui/Modal.tsx" data-component-line="67" data-component-file="Modal.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22XMarkIcon%22%7D" name="XMarkIcon" size={24} />
          </button>
        )}

        {/* Body */}
        <div data-component-id="components/ui/Modal.tsx:72:8" data-component-path="components/ui/Modal.tsx" data-component-line="72" data-component-file="Modal.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-6%20text-slate-900%22%7D" className="p-6 text-slate-900">{children}</div>
      </div>
    </div>
  );
}
